import React from 'react';
import { Globe, Lock, Users } from 'lucide-react';
import { useWhoami } from '@cotera/client/app/stores/org';
import { Tooltip } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ResourceAccessEntry } from '@cotera/client/app/etc/api/typed-client';
import {
  RESOURCE_LIST_CELL_TEXT,
  type FolderScope,
} from './resource-list-types';
import { useOrgMembersById } from './use-org-members';
import { MemberAvatar } from './member-avatar';
import { GranteeAvatarStack, useGrantees } from './grantee-avatar-stack';

/** Shared metrics so every scope state reads as one calm family (Option 1). */
const scopeLayoutClass = 'inline-flex items-center gap-1.5 whitespace-nowrap';

/**
 * One class for every state, private included.
 *
 * The unshared state used to render at `text-muted-foreground/50` so it sat
 * behind the rows that reported real sharing. That was defensible while it drew
 * an em dash — a dash carries no information, so dimming it costs nothing. It is
 * a word now, and at 11px that opacity lands near 1.7:1 against the page, well
 * under the 4.5:1 this size needs. The row's own summary line already made this
 * exact trade and came down the same way.
 *
 * The lock still does the de-emphasising work: a closed padlock reads as "no
 * one" faster than a dimmed word did, without asking anyone to see it at half
 * strength.
 */
const scopeTextClass = cn(scopeLayoutClass, RESOURCE_LIST_CELL_TEXT);

/**
 * Every state leads with a glyph, so the cell announces what kind of fact it is
 * before it is read.
 *
 * This is what replaced the "Shared with" text label the narrow layout used to
 * stamp in front of the value. Two text labels a few pixels apart — "Created by"
 * and "Shared with", each followed by a name — read as one run-on line, and
 * people could not tell which value answered which. A lock, a globe and a pair
 * of figures do the same work without competing with "Created by" for the same
 * kind of attention.
 *
 * `Lock` / `Globe` / `Users` are the set `folder-picker-ask-view` already draws
 * for these three scopes; borrowed rather than re-chosen so the picker and the
 * lists teach the same symbols.
 *
 * `currentColor`, so each state's own text colour carries the glyph — the quiet
 * private state gets a quiet lock.
 */
const SCOPE_ICON_CLASS = 'h-3 w-3 shrink-0';

/**
 * The framing the glyph carries visually, for anything that cannot see it.
 *
 * "Everyone" and a stack of faces mean nothing read aloud on their own, and the
 * label that used to supply that context is gone. "Private" is left out: it is
 * a complete answer by itself.
 */
const SharedWithLabel: React.FC = () => (
  <span className="sr-only">Shared with </span>
);

/**
 * "Owned by {avatar}" — the inbound-share counterpart to the scope badge, shown
 * on Library rows the current user doesn't own. Same muted family + colored
 * avatar ring; hover reveals the owner's name and email.
 */
export const ResourceOwnerBadge: React.FC<{ ownerId: string }> = ({
  ownerId,
}) => {
  const membersById = useOrgMembersById();
  const member = membersById.get(ownerId);
  const name =
    member !== undefined && member.name !== null && member.name.trim() !== ''
      ? member.name
      : null;
  const email = member?.email ?? null;
  const label = name ?? email;

  const tile = (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn(RESOURCE_LIST_CELL_TEXT, 'whitespace-nowrap')}>
        Owned by
      </span>
      <MemberAvatar seed={ownerId} label={label} />
    </span>
  );

  // Owner outside the org (or not yet loaded): no name to reveal, so skip hover.
  if (label === null) {
    return tile;
  }

  return (
    <Tooltip
      side="top"
      asChild
      tooltipContent={
        <div className="flex items-center gap-2 max-w-[240px]">
          <MemberAvatar seed={ownerId} label={label} />
          <div className="flex min-w-0 flex-col">
            <span className="text-xs font-medium text-foreground truncate">
              {label}
            </span>
            {name !== null && email !== null ? (
              <span className="text-xs text-muted-foreground truncate">
                {email}
              </span>
            ) : null}
          </div>
        </div>
      }
    >
      {tile}
    </Tooltip>
  );
};

export const ResourceScopeBadge: React.FC<{
  scope: FolderScope;
  /** Owner + explicit grantees (from list rows); powers the avatar stack + who-has-access hover. */
  users?: ResourceAccessEntry[];
  /** Resource owner id — excluded from the "shared with" stack/list. */
  ownerId?: string;
}> = ({ scope, users, ownerId }) => {
  const orgName = useWhoami((s) => s.org.name);
  const people = useGrantees(users, ownerId);

  // Not shared with anyone.
  //
  // Named, where this used to draw an em dash. A dash is the notation for a
  // value that is missing, and people read it that way — as something that
  // failed to load, or as a state nobody explained. Nothing is missing here:
  // "not shared yet" is the answer, and on a mostly-private list it is the
  // answer most rows carry.
  //
  // "Private" rather than "Not shared" or "Only you" because it is the word
  // `folder-picker-ask-view` already uses for this scope, so the place you set
  // sharing and the place you read it back agree.
  //
  // Still a step quieter than the states that report real sharing — the rows
  // that say something should be the ones that catch the eye.
  if (scope === 'user') {
    return (
      <Tooltip
        side="top"
        asChild
        tooltipContent="Not shared — only you can see this"
      >
        <span className={scopeTextClass}>
          <Lock className={SCOPE_ICON_CLASS} aria-hidden="true" />
          Private
        </span>
      </Tooltip>
    );
  }

  if (scope === 'org') {
    return (
      <Tooltip side="top" asChild tooltipContent={`Everyone at ${orgName}`}>
        <span className={scopeTextClass}>
          <Globe className={SCOPE_ICON_CLASS} aria-hidden="true" />
          <SharedWithLabel />
          Everyone
        </span>
      </Tooltip>
    );
  }

  // external — the specific people it's directly shared with (`people`, minus
  // owner/org).
  //
  // No resolvable people (e.g. link-only share). Says "Specific people" rather
  // than a bare "Shared", which under the "Shared with" column header read as
  // "Shared with: Shared"; this matches the state's own tooltip.
  if (people.length === 0) {
    return (
      <Tooltip side="top" asChild tooltipContent="Shared with specific people">
        <span className={scopeTextClass}>
          <Users className={SCOPE_ICON_CLASS} aria-hidden="true" />
          <SharedWithLabel />
          Specific people
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      side="top"
      asChild
      tooltipContent={
        <div className="flex flex-col gap-1.5 max-w-[260px]">
          <span className="text-xs font-medium text-foreground">
            Shared with {people.length}
          </span>
          {people.map((p) => (
            <div key={p.id} className="flex items-center gap-1.5 min-w-0">
              <MemberAvatar seed={p.id} label={p.label} />
              <span className="text-xs text-muted-foreground truncate">
                {p.label ?? 'Someone outside your org'}
              </span>
            </div>
          ))}
        </div>
      }
    >
      <span className={scopeTextClass}>
        <Users className={SCOPE_ICON_CLASS} aria-hidden="true" />
        <SharedWithLabel />
        <GranteeAvatarStack
          people={people}
          overflowClassName={RESOURCE_LIST_CELL_TEXT}
        />
      </span>
    </Tooltip>
  );
};
